import type { ProposalId } from '@shared/models/proposals.model';
import { Page } from '../store/ui/ui.model';

export const URL_CATEGORY_NONE = 'none';

/**
 * Search-param key holding the currently selected element id (`?selected=<id>`).
 * Missing / `none` means no element is selected. Selection lives in the URL
 * query (not the path) so it is shareable and survives reloads (DEV-3454).
 */
export const SELECTED_ELEMENT_SEARCH_PARAM = 'selected';

/**
 * Known URL query parameters that apply to project / shared project routes.
 * Add new keys here (and to {@link projectQueryParamKeys}) when introducing
 * new persistent query state. Each key is intentionally optional — `undefined`
 * means the param is absent from the URL.
 */
export type ProjectQueryParams = {
  /** Raw selected proposal value; `baseline` is explicit and `undefined` means default. */
  proposal: ProposalId | undefined;
};

/** Ordered list of every key in {@link ProjectQueryParams}. */
export const projectQueryParamKeys = [
  'proposal',
] as const satisfies ReadonlyArray<keyof ProjectQueryParams>;

/**
 * Query-param payload paired with the route type it belongs to.
 * Mirrors the shape of {@link ProjectParamsObject} so both can be derived
 * from the same URL and synced in one pass.
 */
export type ProjectQueryParamsObject =
  | { type: 'project'; data: ProjectQueryParams }
  | { type: 'shared'; data: ProjectQueryParams }
  | { type: 'generation'; data: ProjectQueryParams }
  | { type: 'project-list'; data: ProjectQueryParams }
  | { type: 'error'; data: ProjectQueryParams };

export type NodonProjectFullUrlParams = {
  type: 'project';
  organization: string;
  projectId: string;
  versionId: string;
  page: Page;
  /** Selected element id, sourced from `?selected=`. `none` = no selection. */
  elementId: NonCollapsibleString | typeof URL_CATEGORY_NONE;
};

export type NodonProjectListUrlParams = {
  type: 'project-list';
  organization: string;
};

export type NodonProjectUrlParams =
  NodonProjectFullUrlParams | NodonProjectListUrlParams;

export type ProjectSelectionParams = Pretty<
  Pick<NodonProjectFullUrlParams, 'page' | 'elementId'>
>;

export const isFullProjectUrlParams = (
  params: Partial<NodonProjectFullUrlParams>,
): params is NodonProjectFullUrlParams =>
  params.organization != null &&
  params.projectId != null &&
  params.versionId != null &&
  params.page != null &&
  params.elementId != null;
export const isFullSharedProjectUrlParams = (
  params: Partial<NodonSharedProjectUrlParams>,
): params is NodonSharedProjectUrlParams => {
  return Object.values(params).every((value) => value !== undefined);
};
type NodonProjectUrlParamKey = keyof Omit<NodonProjectFullUrlParams, 'type'>;
export const nodonProjectUrlParamKeys: NodonProjectUrlParamKey[] = [
  'organization',
  'projectId',
  'versionId',
  'page',
  'elementId',
] as const;

export type NodonSharedProjectUrlParams = {
  sharingKey: string;
  versionId: string;
  page: Page;
  elementId: string;
};
type NodonSharedProjectUrlParamKey = keyof NodonSharedProjectUrlParams;
export const nodonSharedProjectUrlParamKeys: NodonSharedProjectUrlParamKey[] = [
  'sharingKey',
  'versionId',
  'page',
  'elementId',
] as const;

export type NodonGenerationProjectUrlParams = {
  generationKey: string;
  versionId: string;
  page: Page;
  elementId: string;
};
type NodonGenerationProjectUrlParamKey = keyof NodonGenerationProjectUrlParams;
export const nodonGenerationProjectUrlParamKeys: NodonGenerationProjectUrlParamKey[] =
  ['generationKey', 'versionId', 'page', 'elementId'] as const;

export const isFullGenerationProjectUrlParams = (
  params: Partial<NodonGenerationProjectUrlParams>,
): params is NodonGenerationProjectUrlParams =>
  Object.values(params).every((value) => value !== undefined);

export type NavigateToFn = (params: NodonProjectUrlParams) => void;
